import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import apiClient from "@/lib/api/apiClient";
import {
  CARET_MIRROR_COPIED_STYLE_PROPERTIES,
  CARET_MIRROR_DIV_ID,
  CARET_MIRROR_FALLBACK_LINE_HEIGHT,
  MENTION_BLUR_CLOSE_DELAY_MS,
  MENTION_DEFAULT_USER_PARAMS,
  MENTION_DROPDOWN_CARET_GAP,
  MENTION_DROPDOWN_FLIP_GAP,
  MENTION_DROPDOWN_HEIGHT,
  MENTION_DROPDOWN_VIEWPORT_INSET,
  MENTION_DROPDOWN_WIDTH,
  MENTION_SEARCH_DEBOUNCE_MS,
  MENTION_USER_ENDPOINT,
} from "./MentionTextarea.constants";

/** Matches Angular note-add `applyTagging` / `detectTagging` */
export function applyTaggingToNotes(
  notes: string,
  tagging: Record<string, string>,
): string {
  let out = notes;
  const keys = Object.keys(tagging).sort((a, b) => b.length - a.length);
  for (const k of keys) {
    const v = tagging[k];
    if (!k || !v) continue;
    out = out.split(k).join(v);
  }
  return out;
}

export interface MentionUser {
  user_id: string | number;
  name: string;
}

function normalizeUsers(raw: unknown): MentionUser[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(Boolean)
    .map((u: unknown) => {
      const row = u as Record<string, unknown>;
      const userId = row.user_id ?? row.id;
      return {
        user_id:
          typeof userId === "string" || typeof userId === "number"
            ? userId
            : String(userId ?? ""),
        name: String(row.name ?? row.username ?? "").trim(),
      };
    })
    .filter((u) => u.name && u.user_id != null);
}

function mergeUsers(base: MentionUser[], extra: MentionUser[]): MentionUser[] {
  const seen = new Set<string>();
  const out: MentionUser[] = [];
  for (const u of [...base, ...extra]) {
    const id = String(u.user_id);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(u);
  }
  return out;
}

function filterByMentionQuery(items: MentionUser[], query: string): MentionUser[] {
  const q = query.toLowerCase();
  return items.filter((u) => u.name.toLowerCase().startsWith(q));
}

/** Parse @mention segment (Angular angular-mentions default: allowSpace false). */
function getMentionSegment(
  text: string,
  caret: number,
): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const at = before.lastIndexOf("@");
  if (at === -1) return null;
  if (at > 0 && !/\s/.test(before[at - 1]!)) return null;
  const afterAt = before.slice(at + 1);
  if (/[\s\n]/.test(afterAt)) return null;
  return { start: at, query: afterAt };
}

/**
 * Calculates (top, left) pixel coordinates of a character position inside a textarea.
 */
function getCaretCoordinates(
  element: HTMLTextAreaElement,
  position: number,
): { top: number; left: number; height: number } {
  const div = document.createElement("div");
  div.id = CARET_MIRROR_DIV_ID;
  document.body.appendChild(div);

  const style = div.style;
  const computed = window.getComputedStyle(element);

  style.whiteSpace = "pre-wrap";
  style.wordWrap = "break-word";
  style.position = "absolute";
  style.visibility = "hidden";

  CARET_MIRROR_COPIED_STYLE_PROPERTIES.forEach((prop) => {
    style[prop] = computed[prop];
  });

  div.textContent = element.value.substring(0, position);

  const span = document.createElement("span");
  span.textContent = element.value.substring(position) || "@";
  div.appendChild(span);

  const coordinates = {
    top: span.offsetTop + parseInt(computed.borderTopWidth, 10),
    left: span.offsetLeft + parseInt(computed.borderLeftWidth, 10),
    height: parseInt(computed.lineHeight, 10) || CARET_MIRROR_FALLBACK_LINE_HEIGHT,
  };

  document.body.removeChild(div);
  return coordinates;
}

export interface MentionTextareaProps {
  value: string;
  onChange: (value: string) => void;
  /** Called when user picks a user — parent should set `taggingContent['@'+name] = '[userid:…]'` */
  onTaggingAdd: (displayKey: string, useridPlaceholder: string) => void;
  dataTestId?: string;
  /** Optional `id` on the textarea for `<label htmlFor>`. */
  textareaId?: string;
  disabled?: boolean;
  rows?: number;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
  /** Classes merged onto the outer wrapper (positioning root). Default includes `relative`. */
  wrapperClassName?: string;
  wrapperStyle?: React.CSSProperties;
}

/**
 * @-mentions for Liberty internal notes — parity with Angular `mentionConfig` + `searchUser` / `applyTagging`.
 */
export function MentionTextarea({
  value,
  onChange,
  onTaggingAdd,
  dataTestId,
  textareaId,
  disabled,
  rows = 5,
  placeholder,
  className,
  style,
  wrapperClassName,
  wrapperStyle,
}: MentionTextareaProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const searchReq = useRef(0);

  const [defaultUsers, setDefaultUsers] = useState<MentionUser[]>([]);
  const defaultUsersRef = useRef<MentionUser[]>([]);
  defaultUsersRef.current = defaultUsers;
  const [mergedUsers, setMergedUsers] = useState<MentionUser[]>([]);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionStart, setMentionStart] = useState(0);
  const [mentionQuery, setMentionQuery] = useState("");
  /** End index of the active @query segment (caret), for correct replace on pick */
  const mentionEndRef = useRef(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [dropdownPos, setDropdownPos] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const initUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const res = await apiClient.get(MENTION_USER_ENDPOINT, {
        params: MENTION_DEFAULT_USER_PARAMS,
      });
      const data = normalizeUsers(
        (res.data as { data?: unknown })?.data ?? res.data,
      );
      setDefaultUsers(data);
      setMergedUsers(data);
    } catch {
      setDefaultUsers([]);
      setMergedUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    void initUsers();
  }, [initUsers]);

  /** Angular `searchUser(searchText)`: GET /api/user with or without username, merge into default list. */
  const searchUsers = useCallback(
    async (searchText: string) => {
      const id = ++searchReq.current;
      setLoadingUsers(true);
      try {
        const params = searchText
          ? { username: searchText }
          : ({} as Record<string, string>);
        const res = await apiClient.get(MENTION_USER_ENDPOINT, { params });
        if (id !== searchReq.current) return;
        const page = normalizeUsers(
          (res.data as { data?: unknown })?.data ?? res.data,
        );
        setMergedUsers(mergeUsers(defaultUsersRef.current, page));
      } catch {
        if (id === searchReq.current) setMergedUsers(defaultUsersRef.current);
      } finally {
        if (id === searchReq.current) setLoadingUsers(false);
      }
    },
    [],
  );

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!mentionOpen) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void searchUsers(mentionQuery);
    }, MENTION_SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [mentionOpen, mentionQuery, searchUsers]);

  const filtered = useMemo(
    () => filterByMentionQuery(mergedUsers, mentionQuery),
    [mergedUsers, mentionQuery],
  );

  useEffect(() => {
    setSelectedIndex(0);
  }, [mentionQuery, mentionOpen, filtered.length]);

  useEffect(() => {
    if (mentionOpen && listRef.current && filtered.length > 0) {
      const selectedEl = listRef.current.children[selectedIndex] as HTMLElement;
      if (selectedEl) {
        selectedEl.scrollIntoView({ block: "nearest" });
      }
    }
  }, [selectedIndex, mentionOpen, filtered.length]);

  const updateDropdownRect = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();

    try {
      const coords = getCaretCoordinates(el, mentionStart);
      let top =
        r.top + coords.top - el.scrollTop + coords.height + MENTION_DROPDOWN_CARET_GAP;
      let left = r.left + coords.left - el.scrollLeft;

      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      // Keep within horizontal window bounds
      if (left + MENTION_DROPDOWN_WIDTH > windowWidth - MENTION_DROPDOWN_VIEWPORT_INSET) {
        left = Math.max(
          MENTION_DROPDOWN_VIEWPORT_INSET,
          windowWidth - MENTION_DROPDOWN_WIDTH - MENTION_DROPDOWN_VIEWPORT_INSET,
        );
      }
      if (left < MENTION_DROPDOWN_VIEWPORT_INSET) {
        left = MENTION_DROPDOWN_VIEWPORT_INSET;
      }

      // If dropdown would extend past bottom of screen, show above caret if space exists
      const flippedTop =
        top - MENTION_DROPDOWN_HEIGHT - coords.height - MENTION_DROPDOWN_FLIP_GAP;
      if (
        top + MENTION_DROPDOWN_HEIGHT > windowHeight - MENTION_DROPDOWN_VIEWPORT_INSET &&
        flippedTop > MENTION_DROPDOWN_VIEWPORT_INSET
      ) {
        top = flippedTop;
      }

      setDropdownPos({
        top,
        left,
        width: MENTION_DROPDOWN_WIDTH,
      });
    } catch {
      setDropdownPos({
        top: r.bottom + MENTION_DROPDOWN_CARET_GAP,
        left: r.left,
        width: Math.max(r.width, MENTION_DROPDOWN_WIDTH),
      });
    }
  }, [mentionStart]);

  useLayoutEffect(() => {
    if (!mentionOpen) {
      setDropdownPos(null);
      return;
    }
    updateDropdownRect();
  }, [mentionOpen, value, updateDropdownRect]);

  useEffect(() => {
    if (!mentionOpen) return;
    const onScroll = () => updateDropdownRect();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [mentionOpen, updateDropdownRect]);

  useEffect(() => {
    if (!mentionOpen) return;
    const close = (e: MouseEvent) => {
      const t = e.target as Node;
      if (containerRef.current?.contains(t)) return;
      setMentionOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [mentionOpen]);

  const openMentionFromCaret = useCallback(
    (text: string, caretPos: number) => {
      const seg = getMentionSegment(text, caretPos);
      if (!seg) {
        setMentionOpen(false);
        return;
      }
      setMentionStart(seg.start);
      setMentionQuery(seg.query);
      mentionEndRef.current = caretPos;
      setMentionOpen(true);
    },
    [],
  );

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const v = e.target.value;
    const sel = e.target.selectionStart ?? v.length;
    onChange(v);
    openMentionFromCaret(v, sel);
  };

  const handleSelectUser = (user: MentionUser) => {
    const ta = textareaRef.current;
    const end = mentionEndRef.current;
    const start = mentionStart;
    const before = value.slice(0, start);
    const after = value.slice(end);
    const insertion = `@${user.name}`;
    const next = before + insertion + after;
    onChange(next);
    onTaggingAdd(`@${user.name}`, `[userid:${user.user_id}]`);
    setMentionOpen(false);
    requestAnimationFrame(() => {
      if (!ta) return;
      const pos = before.length + insertion.length;
      ta.focus();
      ta.setSelectionRange(pos, pos);
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!mentionOpen || filtered.length === 0) {
      if (e.key === "Escape") setMentionOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((i) => (i + 1) % filtered.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex(
        (i) => (i - 1 + filtered.length) % filtered.length,
      );
    } else if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      const pick = filtered[selectedIndex];
      if (pick) handleSelectUser(pick);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setMentionOpen(false);
    }
  };

  const handleClick = () => {
    const ta = textareaRef.current;
    if (!ta) return;
    const sel = ta.selectionStart ?? value.length;
    openMentionFromCaret(value, sel);
  };

  const handleBlur = () => {
    window.setTimeout(() => {
      const ta = textareaRef.current;
      if (document.activeElement === ta) return;
      setMentionOpen(false);
    }, MENTION_BLUR_CLOSE_DELAY_MS);
  };

  return (
    <div
      ref={containerRef}
      className={["relative", wrapperClassName].filter(Boolean).join(" ")}
      style={wrapperStyle}
    >
      <textarea
        ref={textareaRef}
        id={textareaId}
        rows={rows}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onClick={handleClick}
        onKeyUp={handleClick}
        onBlur={handleBlur}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete="off"
        data-testid={dataTestId}
        className={className}
        style={style}
      />
      {mentionOpen && dropdownPos && (
        <ul
          ref={listRef}
          className="fixed z-[10050] max-h-56 overflow-y-auto rounded-xl border-2 border-[rgb(21_151_255)]/30 bg-white shadow-2xl shadow-slate-900/20 ring-1 ring-black/5"
          style={{
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
          }}
          role="listbox"
        >
          <div className="sticky top-0 z-10 flex items-center justify-between border-b border-blue-100 bg-[#eff6ff] px-3.5 py-1.5 text-[11px] font-bold text-[#1597ff] uppercase tracking-wider">
            <span>Mention User</span>
            <span className="text-[10px] font-medium text-slate-400">↑↓ to navigate</span>
          </div>
          {loadingUsers && filtered.length === 0 ? (
            <li className="px-3.5 py-2.5 text-sm text-slate-500 italic">Loading users…</li>
          ) : filtered.length === 0 ? (
            <li className="px-3.5 py-2.5 text-sm text-slate-500 italic">No matching users</li>
          ) : (
            filtered.map((u, idx) => {
              const isSelected = idx === selectedIndex;
              return (
                <li key={String(u.user_id)}>
                  <button
                    type="button"
                    className={`w-full px-3.5 py-2 text-left text-sm transition-colors flex items-center gap-2.5 ${
                      isSelected
                        ? "bg-[#eaf4ff] text-[#1597ff] font-semibold border-l-4 border-[#1597ff]"
                        : "text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                    }`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelectUser(u);
                    }}
                  >
                    <span className="truncate">{u.name}</span>
                  </button>
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}
